/**
 * The one owner of `navigator.mediaSession` — the OS transport the lock screen, the notification
 * shade and the AirPods' squeeze all drive. There is exactly one of it per tab, and as of M4c two
 * things in this app want it: the TTS bar (`lib/tts-player.ts`, owner 'tts') and the <video> of an
 * uploaded file (`lib/player/html5.ts`, owner 'video'). If both talked to the platform directly,
 * whoever spoke last would leave the other's title on the lock screen and, worse, action handlers
 * still wired to a player that has since stopped — a ▶ that does nothing is worse than no ▶.
 *
 * So ownership is arbitrated here. A surface claims the session when the learner actually starts
 * it and releases it when it stops; every setter is a silent no-op for anyone who is not the
 * current owner, so a background clip can never overwrite the foreground video's metadata.
 * `nextOwner` is that rule as a pure function (last claim wins; a release by a non-owner changes
 * nothing) and is what the test covers.
 *
 * Everything is best-effort by construction: a browser with no Media Session API, or with no
 * support for one particular action, must cost the caller nothing. Nothing here touches the DOM
 * at import time — the web tests run in node, where the API does not exist and every call is inert.
 */

export type MediaSessionOwner = 'tts' | 'video';

/**
 * An action handler. Widened from `() => void` so a handler can read the details the platform
 * passes for `seekto`/`seekbackward`/`seekforward`; a plain `() => void` is still assignable.
 */
export type MediaSessionHandler = (details?: MediaSessionActionDetails) => void;

export interface MediaSessionMeta {
  title: string;
  artist?: string;
  album?: string;
}

/**
 * The arbitration rule, pure. Claiming always wins — the thing the learner just started is the
 * thing the lock screen should show — and releasing only does something for the holder, so a
 * player that was already displaced cannot yank the session away from the one that displaced it.
 */
export function nextOwner(
  current: MediaSessionOwner | null,
  action: { type: 'claim' | 'release'; owner: MediaSessionOwner },
): MediaSessionOwner | null {
  if (action.type === 'claim') return action.owner;
  return current === action.owner ? null : current;
}

let current: MediaSessionOwner | null = null;
/** Actions we installed a handler for, so a hand-over can take them all back down. */
let installed: string[] = [];

function session(): MediaSession | null {
  try {
    return typeof navigator !== 'undefined' && 'mediaSession' in navigator
      ? navigator.mediaSession
      : null;
  } catch {
    return null;
  }
}

/** Leave the platform as we found it: no metadata, no playback state, no dangling handlers. */
function clearPlatform(): void {
  const ms = session();
  const actions = installed;
  installed = [];
  if (!ms) return;
  for (const action of actions) {
    try {
      ms.setActionHandler(action as MediaSessionAction, null);
    } catch {
      /* the action was never supported here; nothing to take down */
    }
  }
  try {
    ms.metadata = null;
  } catch {
    /* best effort */
  }
  try {
    ms.playbackState = 'none';
  } catch {
    /* best effort */
  }
}

/** Take the session (last claim wins). The new owner is expected to set its own metadata next. */
export function claimMediaSession(owner: MediaSessionOwner): void {
  if (current === owner) return;
  // Hand-over: the previous owner's metadata and handlers must not outlive its claim, and the
  // new owner may not install a handler for every action the old one did.
  if (current !== null) clearPlatform();
  current = nextOwner(current, { type: 'claim', owner });
}

/** Give the session back. A no-op unless `owner` currently holds it. */
export function releaseMediaSession(owner: MediaSessionOwner): void {
  const next = nextOwner(current, { type: 'release', owner });
  if (next === current) return;
  clearPlatform();
  current = next;
}

export function ownsMediaSession(owner: MediaSessionOwner): boolean {
  return current === owner;
}

export function setMediaSessionMetadata(
  owner: MediaSessionOwner,
  meta: MediaSessionMeta | null,
): void {
  if (!ownsMediaSession(owner)) return;
  const ms = session();
  if (!ms) return;
  try {
    ms.metadata =
      meta && typeof MediaMetadata !== 'undefined'
        ? new MediaMetadata({
            title: meta.title,
            artist: meta.artist ?? '',
            album: meta.album ?? '',
          })
        : null;
  } catch {
    /* best effort */
  }
}

export function setMediaSessionPlaybackState(
  owner: MediaSessionOwner,
  state: 'none' | 'playing' | 'paused',
): void {
  if (!ownsMediaSession(owner)) return;
  const ms = session();
  if (!ms) return;
  try {
    ms.playbackState = state;
  } catch {
    /* best effort */
  }
}

/**
 * Install (or, with a null value, remove) action handlers. Keyed by the platform's action names
 * ('play', 'pause', 'stop', 'seekbackward', 'seekforward', 'seekto', …) — one unsupported action
 * throws on its own and must not stop the rest from being installed.
 */
export function setMediaSessionHandlers(
  owner: MediaSessionOwner,
  handlers: Record<string, MediaSessionHandler | null>,
): void {
  if (!ownsMediaSession(owner)) return;
  const ms = session();
  if (!ms) return;
  for (const [action, fn] of Object.entries(handlers)) {
    try {
      ms.setActionHandler(action as MediaSessionAction, fn as MediaSessionActionHandler | null);
      const at = installed.indexOf(action);
      if (fn && at === -1) installed.push(action);
      else if (!fn && at !== -1) installed.splice(at, 1);
    } catch {
      /* action unsupported on this browser */
    }
  }
}
